// The wind arrow is a signed-distance-field icon, so MapLibre can fill it with the station's wind colour and
// outline it with a halo. The circle itself is a circle layer with an unbroken stroke; the arrow is cut off along the
// stroke's outer edge, so it touches the circle without lying under its (see-through) fill.

/** Device pixels per CSS pixel of the images. */
export const MARKER_PIXEL_RATIO = 2;
/** Circle radius and stroke width, CSS px: fits "25" plus a small "+15". */
export const MARKER_RADIUS = 16;
export const MARKER_STROKE = 1.5;
/** Arrow: base width, how far its tip sticks out of the circle and its base reaches in, CSS px. */
const ARROW_BASE = 14.4;
const ARROW_OUT = 8.55;
const ARROW_IN = 3.6;
/** Room around the shape for the distance falloff (halo), CSS px. */
const BUFFER = 4;
/** MapLibre's SDF encoding (as in its glyphs): edge at 0.75, `RADIUS` image px of falloff. */
const RADIUS = 8;
const CUTOFF = 0.25;

type Vec = [number, number];

/** Signed distance from p to triangle abc (negative inside). */
function triangleDistance(p: Vec, a: Vec, b: Vec, c: Vec): number {
  const edges: [Vec, Vec][] = [
    [a, b],
    [b, c],
    [c, a],
  ];
  let d = Infinity;
  let inside = true;
  for (const [u, v] of edges) {
    const ex = v[0] - u[0];
    const ey = v[1] - u[1];
    const px = p[0] - u[0];
    const py = p[1] - u[1];
    const t = Math.max(0, Math.min(1, (px * ex + py * ey) / (ex * ex + ey * ey)));
    d = Math.min(d, Math.hypot(px - ex * t, py - ey * t));
    if (ex * py - ey * px < 0) inside = false;
  }
  return inside ? -d : d;
}

/**
 * The arrow as an RGBA image whose alpha holds the distance field, pointing up (north, before rotation).
 * Square and centred on the circle, so it rotates about the circle's centre.
 */
export function arrowImage() {
  const r = MARKER_PIXEL_RATIO;
  const size = 2 * Math.ceil((MARKER_RADIUS + ARROW_OUT + BUFFER) * r);
  const c = size / 2;
  const radius = (MARKER_RADIUS + MARKER_STROKE) * r;
  // Clockwise in image coordinates (y down): tip, base right, base left.
  const tip: Vec = [c, c - (MARKER_RADIUS + ARROW_OUT) * r];
  const baseY = c - (MARKER_RADIUS - ARROW_IN) * r;
  const right: Vec = [c + (ARROW_BASE / 2) * r, baseY];
  const left: Vec = [c - (ARROW_BASE / 2) * r, baseY];

  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const p: Vec = [x + 0.5, y + 0.5];
      // Triangle minus the disc under the circle and its stroke.
      const d = Math.max(triangleDistance(p, tip, right, left), radius - Math.hypot(p[0] - c, p[1] - c));
      const v = Math.max(0, Math.min(255, Math.round(255 - 255 * (d / RADIUS + CUTOFF))));
      data.fill(v, (y * size + x) * 4, (y * size + x) * 4 + 4);
    }
  return { width: size, height: size, data };
}
